import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import CloseIcon from "../../../../components/icons/CloseIcon";
import { RootState } from "../../../../redux/store";
import "./PropertyBankFilter.scss";
import { CommonEnums } from "../../../../enums/common.enums";
import SearchFilterSelector from "../../filter/SearchFilterSelector";
import { commonAction } from "../../../../redux/features/commonSlice";
import { PropertyApi } from "../../../../utils/api/Property";
import FixDataFilterSelector from "./FixDataFilterSelector";
import FilterDates from "./FilterDates";

const PropertyBankFilter = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [propertyTypesList, setPropertyTypesList] = useState<any[]>([]);

  const homePageFilter = useSelector(
    (state: RootState) => state?.common?.homePageFilter,
  );
  const resetFilter = useSelector(
    (state: RootState) => state?.common?.resetFilter,
  );

  const onCloseFilter = () => {
    dispatch(commonAction.setHomePageFilter());
  };

  useEffect(() => {
    const fetchSelectors = async () => {
      try {
        const res = await PropertyApi.getSelectors();
        if (res?.data?.success) {
          const data = res.data.data.property_types;
          const combinedTypes = [
            ...(data.residential || []),
            ...(data.commercial || []),
          ];
          setPropertyTypesList(combinedTypes);
        }
      } catch (err) {
        console.error("Failed to fetch property types", err);
      }
    };
    fetchSelectors();
  }, []);

  const onSubmitHandler = (values: any) => {
    let propertyTypeName = "";

    if (values.property_type) {
      const selectedItem = propertyTypesList.find(
        (item: any) => item._id === values.property_type,
      );

      if (selectedItem) {
        propertyTypeName = selectedItem.name;
      }
    }

    let navigationString = `property-bank?agency_id=${
      values?.agency_id || ""
    }&agency_name=${encodeURIComponent(
      values?.agency_name || "",
    )}&agent_id=${values?.agent_id || ""}&agent_name=${encodeURIComponent(
      values?.agent_name || "",
    )}&property_type=${
      values?.property_type || ""
    }&property_type_name=${encodeURIComponent(
      propertyTypeName || values?.property_type_name || "",
    )}&status=${values?.status || ""}&selected_city_ids=${
      values?.selected_city_ids?.toString() || ""
    }&start_date=${
      values?.startDate === "00-00-00" ? "" : values?.startDate
    }&end_date=${values?.endDate === "00-00-00" ? "" : values?.endDate}`;

    navigate(`/${navigationString}`);
    dispatch(commonAction.resetFilter());
    onCloseFilter();
  };

  const initialValues: any = {
    agency_id: "",
    agency_name: "",
    agent_id: "",
    agent_name: "",
    property_type: "",
    property_type_name: "",
    status: "",
    registration_status: "",
    startDate: "",
    endDate: "",
    selected_city_ids: [],
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      agency_id: Yup.string().optional(),
      agency_name: Yup.string().optional(),
      agent_id: Yup.string().optional(),
      agent_name: Yup.string().optional(),
      property_type: Yup.string().optional(),
      status: Yup.string().optional(),
      startDate: Yup.string().optional(),
      endDate: Yup.string().optional(),
      selected_city_ids: Yup.array().optional(),
    }),
    onSubmit: onSubmitHandler,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
  });

  const onClickClear = () => {
    formik.setValues({
      agency_id: "",
      agency_name: "",
      agent_id: "",
      agent_name: "",
      property_type: "",
      property_type_name: "",
      status: "",
      registration_status: "",
      startDate: "00-00-00",
      endDate: "00-00-00",
      selected_city_ids: [],
    });

    navigate("/property-bank");
    onCloseFilter();
    dispatch(commonAction.resetFilter());
  };

  useEffect(() => {
    let agency_id = searchParams.get("agency_id");
    let agency_name = searchParams.get("agency_name");
    let agent_id = searchParams.get("agent_id");
    let agent_name = searchParams.get("agent_name");
    let property_type = searchParams.get("property_type");
    let property_type_name = searchParams.get("property_type_name");
    let status = searchParams.get("status");
    let selected_city_ids = searchParams.get("selected_city_ids");
    let start_date = searchParams.get("start_date") || "";
    let end_date = searchParams.get("end_date") || "";

    formik.setValues({
      agency_id: agency_id ? agency_id : "",
      agency_name: agency_name ? agency_name : "",
      agent_id: agent_id ? agent_id : "",
      agent_name: agent_name ? agent_name : "",
      property_type: property_type ? property_type : "",
      property_type_name: property_type_name ? property_type_name : "",
      status: status ? status : "",
      registration_status: "",
      startDate: start_date,
      endDate: end_date,
      selected_city_ids: selected_city_ids
        ? selected_city_ids.split(",").filter(Boolean)
        : [],
    });
  }, [resetFilter, searchParams]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <div
        className={`invites-filter-container ${
          homePageFilter ? "" : "deactivate-filter"
        }`}
      >
        {homePageFilter && (
          <div className="filter-background" onClick={onCloseFilter}></div>
        )}

        <div
          className={`invites-filter-box ${
            homePageFilter ? "" : "deactivate-filter"
          }`}
        >
          <div className="filter-top-heading">
            <p className="filter-top-head">Filters</p>
            <div className="filter-close-icon" onClick={onCloseFilter}>
              <CloseIcon />
            </div>
          </div>

          <div className="filter-body-container">
            <div className="filter-selector-container">
              <SearchFilterSelector
                formik={formik}
                lable="Agency"
                formik_key_id="agency_id"
                formik_key_name="agency_name"
              />
              <div style={{ marginTop: 24 }}>
                <SearchFilterSelector
                  formik={formik}
                  lable="Agent"
                  formik_key_id="agent_id"
                  formik_key_name="agent_name"
                />
              </div>

              <FixDataFilterSelector
                formik={formik}
                type={
                  CommonEnums?.selectorType?.property_type || "property_type"
                }
                placeholder="Property type"
                customData={propertyTypesList}
              />

              <FixDataFilterSelector
                formik={formik}
                type={CommonEnums.selectorType?.status}
                placeholder="Status"
              />
            </div>

            <FilterDates formik={formik} />
          </div>

          <div className="action-btn">
            <button
              className="clear-filters-btn"
              onClick={onClickClear}
              type="button"
            >
              Clear Filters
            </button>
            <button className="apply-btn" type="submit">
              Apply
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PropertyBankFilter;
